/**
 * The confirm button, kept in view at the foot of the screen however long the field list is.
 *
 * Beside it is always a sentence saying where things stand: why confirming is not possible yet, what
 * confirming will do, or that there is nothing left to confirm. A disabled button with no reason
 * looks the same whether the work is finished or blocked.
 */
import { CheckCircle2 } from 'lucide-react';
import { Button, Spinner } from '@design-system';
import { fieldMapCopy } from '../lib/field-map-copy';

interface FieldMapActionBarProps {
  pendingCount: number;
  /** How many of the pending rows clear a mapping, which is a change but not a mapping. */
  removalCount: number;
  missingScaleCount: number;
  hasMappedRows: boolean;
  hasConsent: boolean;
  hasUnsavedEdits: boolean;
  isSaving: boolean;
  onSave: () => void;
  onDiscard: () => void;
}

export function FieldMapActionBar({
  pendingCount,
  removalCount,
  missingScaleCount,
  hasMappedRows,
  hasConsent,
  hasUnsavedEdits,
  isSaving,
  onSave,
  onDiscard,
}: FieldMapActionBarProps) {
  const { actions } = fieldMapCopy;
  const nothingPending = pendingCount === 0;
  const blocked = !hasConsent
    ? actions.needsConsent
    : missingScaleCount > 0
      ? actions.needsUnits(missingScaleCount)
      : null;

  return (
    <div className="sticky bottom-0 z-10 -mx-4 mt-6 border-t border-gray-200 bg-white/95 px-4 py-3 backdrop-blur sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
      {/* The right padding keeps the buttons clear of the support launcher in the corner. */}
      <div className="flex flex-col gap-3 pr-20 sm:flex-row sm:items-center sm:justify-between">
        <p
          role="status"
          className="flex items-center gap-2 text-sm text-gray-700"
        >
          {nothingPending ? (
            <>
              {hasMappedRows && <CheckCircle2 className="h-4 w-4 shrink-0 text-green-700" />}
              {hasMappedRows ? actions.allConfirmed : actions.nothingMapped}
            </>
          ) : (
            (blocked ?? actions.ready)
          )}
        </p>
        <div className="flex items-center gap-2">
          {hasUnsavedEdits && (
            <Button
              variant="ghost"
              onClick={onDiscard}
              disabled={isSaving}
            >
              {actions.discard}
            </Button>
          )}
          {!nothingPending && (
            <Button
              onClick={onSave}
              disabled={isSaving || blocked !== null}
            >
              {isSaving ? (
                <>
                  <Spinner aria-hidden="true" />
                  {actions.saving}
                </>
              ) : (
                actions.confirm(pendingCount, removalCount > 0)
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
